import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Polygon } from 'geojson';
import type {
  ImportMatchResult,
  ImportSubsetResult,
  SelectiveImportSession,
} from '../data/import/importPreflight';
import {
  findSpatialMatches,
  IMPORT_METHODS,
  itemCount,
  preparedSelectiveSession,
  type ImportMethod,
  type PreparedImportItem,
} from '../data/import/importWorkflow';
import { viewForExtent } from '../map/viewForExtent';
import type { MapView } from '../map/model';
import { useAppStore } from '../app/state/useAppStore';
import { DialogShell } from './DialogShell';
import { SelectiveImportDialog } from './SelectiveImportDialog';
import { ImportMapContext } from './ImportMapContext';
import { Icon } from './Icon';
import { ImportMethodHelp } from './ImportMethodHelp';
import { ImportMetadata } from './ImportMetadata';

interface Props {
  readonly items: readonly PreparedImportItem[];
  readonly initialMethod: ImportMethod | null;
  readonly onCancel: () => void;
  readonly onCommit: (matches?: ImportMatchResult, fields?: readonly string[]) => void;
  readonly returnFocusTo: HTMLElement | null;
  readonly selectiveSession?: SelectiveImportSession;
  readonly onImportSubset?: (
    subset: ImportSubsetResult,
    options: {
      readonly signal: AbortSignal;
      readonly onProgress: (value: import('../data/import/importPreflight').ImportProgress) => void;
    },
  ) => Promise<void>;
  readonly spatialScan?:
    ((rectangle: Polygon, signal: AbortSignal) => Promise<ImportMatchResult>) | undefined;
  readonly error?: string | null | undefined;
  readonly committing?: boolean;
  readonly spatialExtent?: import('../data/model').SpatialExtent | null | undefined;
}

export function ImportScopeDialog({
  items,
  initialMethod,
  onCancel,
  onCommit,
  returnFocusTo,
  selectiveSession,
  onImportSubset,
  spatialScan,
  error,
  committing = false,
  spatialExtent,
}: Props) {
  const [method, setMethod] = useState<ImportMethod | null>(
    initialMethod === 'all' &&
      selectiveSession &&
      selectiveSession.preflight.fullImportSafety !== 'SAFE_FULL'
      ? null
      : initialMethod,
  );
  const session = useMemo(
    () => selectiveSession ?? preparedSelectiveSession(items),
    [items, selectiveSession],
  );
  const count =
    session.preflight.featureCount ??
    (items.length ? items.reduce((sum, item) => sum + itemCount(item), 0) : null);
  const spatial =
    spatialScan !== undefined ||
    items.some((item) => 'data' in item && item.geometry.renderableFeatureCount > 0);
  const titleId = useId();
  const titleFocusRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    // Method cards disappear on confirmation; retain keyboard focus inside the dialog.
    titleFocusRef.current?.focus();
  }, [method]);
  if (method === 'attribute')
    return (
      <SelectiveImportDialog
        session={session}
        onClose={onCancel}
        onBack={() => setMethod(null)}
        returnFocusTo={returnFocusTo}
        onImport={
          onImportSubset ??
          (async (subset, options) => {
            options.signal.throwIfAborted();
            onCommit(subset, subset.selectedFields);
          })
        }
      />
    );
  if (method === 'spatial' && spatial)
    return (
      <SpatialImportPanel
        items={items}
        onCancel={onCancel}
        onBack={() => setMethod(null)}
        onCommit={onCommit}
        returnFocusTo={returnFocusTo}
        spatialScan={spatialScan}
        committing={committing}
        spatialExtent={spatialExtent}
        error={error}
      />
    );
  return (
    <DialogShell
      className="new-map-dialog import-scope-dialog"
      titleId={titleId}
      onClose={onCancel}
      returnFocusTo={returnFocusTo}
      initialFocusRef={titleFocusRef}
    >
      <h2 id={titleId} ref={titleFocusRef} tabIndex={-1}>
        {method === 'all' ? 'Confirm import' : 'Choose import method'}
      </h2>
      <ImportMetadata
        file={session.preflight.sourceFileName}
        layers={
          items.length
            ? items.map((item) =>
                'data' in item ? item.suggestedLayerName : item.suggestedTableName,
              )
            : [session.preflight.suggestedLayerName]
        }
        count={count}
        scope={method === 'all' ? 'Full dataset' : undefined}
      />
      {session.preflight.fullImportSafety === 'SELECTIVE_ONLY' ? (
        <p className="style-notice">
          This dataset is too large to import safely in full. Choose a smaller subset.
        </p>
      ) : null}
      {error ? (
        <p className="import-error" role="alert">
          {error}
        </p>
      ) : null}
      {items
        .flatMap((item) => item.warnings.map((warning) => warning.message))
        .map((message, index) => (
          <p className="style-notice" key={index}>
            {message}
          </p>
        ))}
      {method === 'spatial' && !spatial ? (
        <p role="status">
          Spatial filtering needs geometry. Prepare this file as points to draw an import area, or
          use full or attribute import for this table.
        </p>
      ) : null}
      {method !== 'all' ? (
        <div className="import-method-list">
          {IMPORT_METHODS.map((option) => (
            <div className="import-method-card" key={option.id}>
              <button
                className="import-method-action"
                aria-label={option.label}
                aria-describedby={`${titleId}-${option.id}-description`}
                type="button"
                disabled={
                  committing ||
                  (option.id === 'spatial' && !spatial) ||
                  (option.id === 'all' && session.preflight.fullImportSafety !== 'SAFE_FULL')
                }
                onClick={() => setMethod(option.id)}
              >
                <Icon name={option.icon} />
                <span>
                  <strong>{option.label}</strong>
                  <small id={`${titleId}-${option.id}-description`}>{option.description}</small>
                </span>
              </button>
              <ImportMethodHelp label={option.label} text={option.help} />
            </div>
          ))}
        </div>
      ) : (
        <div className="import-dialog-actions">
          <button type="button" onClick={() => setMethod(null)}>
            Change method
          </button>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="primary-action"
            type="button"
            disabled={committing}
            onClick={() => onCommit()}
          >
            {count === null ? 'Import dataset' : `Import ${count.toLocaleString()} records`}
          </button>
        </div>
      )}
      {!spatial ? (
        <p className="text-muted">
          Spatial filtering requires a spatial layer or coordinate-derived points.
        </p>
      ) : null}
      {method !== 'all' ? (
        <div className="import-dialog-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      ) : null}
    </DialogShell>
  );
}

function SpatialImportPanel({
  items,
  onCancel,
  onBack,
  onCommit,
  returnFocusTo,
  spatialScan,
  committing,
  spatialExtent,
  error: commitError,
}: Pick<
  Props,
  | 'items'
  | 'onCancel'
  | 'onCommit'
  | 'returnFocusTo'
  | 'spatialScan'
  | 'committing'
  | 'error'
  | 'spatialExtent'
> & {
  readonly onBack: () => void;
}) {
  const { setInteraction } = useContext(ImportMapContext);
  const initialView = useAppStore((state) => state.project.map.view);
  const [view, setView] = useState<MapView>(initialView);
  const [rectangle, setRectangle] = useState<Polygon | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [matches, setMatches] = useState<ImportMatchResult | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const callbacks = useRef({ onCancel });
  useEffect(() => {
    callbacks.current = { onCancel };
  }, [onCancel]);
  useEffect(() => {
    return () => {
      setInteraction(null);
      if (returnFocusTo?.isConnected) returnFocusTo.focus();
    };
  }, [setInteraction, returnFocusTo]);
  useEffect(() => {
    setInteraction({
      drawing,
      rectangle,
      view,
      onViewChange: setView,
      onRectangle: (area) => {
        setRectangle(area);
        setDrawing(false);
      },
      onEscape: () => {
        if (drawing) {
          setDrawing(false);
          setRectangle(null);
        } else callbacks.current.onCancel();
      },
    });
  }, [drawing, rectangle, view, setInteraction]);
  useEffect(() => {
    const controller = new AbortController();
    if (!rectangle) return;
    let active = true;
    queueMicrotask(() => {
      if (active) {
        setWorking(true);
        setMatches(null);
        setError(null);
      }
    });
    void (
      spatialScan
        ? spatialScan(rectangle, controller.signal)
        : findSpatialMatches(items, rectangle, { signal: controller.signal })
    )
      .then((result) => {
        if (active) setMatches(result);
      })
      .catch((cause) => {
        if (active)
          setError(cause instanceof Error ? cause.message : 'Could not scan these geometries.');
      })
      .finally(() => {
        if (active) setWorking(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [items, rectangle, spatialScan]);
  useEffect(() => {
    panelRef.current?.focus();
  }, []);
  const clear = () => {
    setRectangle(null);
    setMatches(null);
    setWorking(false);
    setError(null);
    setDrawing(false);
  };
  // Prepared extents only; no source is mounted in MapLibre to preview the dataset.
  const extents = items.flatMap((item) => ('data' in item && item.extent ? [item.extent] : []));
  const extent = spatialExtent ?? (extents.length ? extents[0] : null);
  return createPortal(
    <div
      className="spatial-import-panel"
      role="region"
      aria-label="Spatial import"
      tabIndex={-1}
      ref={panelRef}
    >
      <strong>Import by spatial filter</strong>
      <p>
        {drawing
          ? 'Drag to draw an import area. Escape cancels drawing.'
          : 'Navigate or zoom, then draw an import area.'}
      </p>
      <p>Intersecting features are imported whole; geometry is not clipped.</p>
      <p role="status">
        {working
          ? 'Finding intersecting features…'
          : matches
            ? `${matches.matchCount.toLocaleString()} of ${matches.totalFeatureCount.toLocaleString()} records intersect${matches.matchCount === 0 ? '. No matches — redraw the area.' : '.'}`
            : 'Draw an area to see the matching count.'}
      </p>
      {error || commitError ? (
        <p className="import-error" role="alert">
          {error || commitError}
        </p>
      ) : null}
      <div className="spatial-import-actions">
        <button
          type="button"
          onClick={() => {
            clear();
            setDrawing(true);
          }}
        >
          {rectangle ? 'Redraw' : 'Draw area'}
        </button>
        <button type="button" onClick={clear} disabled={!rectangle && !drawing}>
          Clear
        </button>
        {extent ? (
          <button type="button" onClick={() => setView(viewForExtent(extent))}>
            View dataset extent
          </button>
        ) : null}
      </div>
      <div className="import-dialog-actions">
        <button type="button" onClick={onBack}>
          Change method
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="primary-action"
          disabled={working || committing || !matches?.matchCount || drawing}
          onClick={() => matches && onCommit(matches)}
        >
          Import {matches?.matchCount ? matches.matchCount.toLocaleString() : ''} features
        </button>
      </div>
    </div>,
    document.body,
  );
}
